'use client';

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Text,
  Button,
  Stack,
  Group,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useCurrentUser } from '@/hooks/use-current-user';
import profileService from '@/services/profileService';
import FAIcon from '../FAIcon';

export const CookieConsentModal: React.FC = () => {
  const theme = useMantineTheme();
  const [user] = useCurrentUser();
  const [opened, { open, close }] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkCookieConsent = async () => {
      const hasConsent = localStorage.getItem('cookieConsent');
      if (!hasConsent) {
        open();
      }
    };

    checkCookieConsent();
  }, [open]);

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      localStorage.setItem('cookieConsent', 'true');

      if (user) {
        await profileService.updateUserProfile(user.uid, {
          cookiePreferences: {
            necessary: true,
            analytics: true,
            marketing: true
          },
          updatedAt: new Date(),
        });
      }

      close();
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      size="100%"
      centered={false}
      withCloseButton={false}
      padding="md"
      radius={0}
      styles={{
        root: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          margin: 0,
          maxWidth: '100%',
          height: 'auto',
          top: 'auto',
          transform: 'none',
          zIndex: 1000,
        },
        content: {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          margin: 0,
        },
        body: {
          padding: 0,
        },
        overlay: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          top: 'auto',
          height: 'auto',
        }
      }}
    >
      <Stack gap="md" p="md">
        <Group justify="space-between" wrap="nowrap">
          <Group>
            <FAIcon
              name="cookie"
              size={24}
              color={theme.colors.blue[6]}
            />
            <div>
              <Title order={4} mb={4}>Cookie Consent</Title>
              <Text size="sm" c="dimmed" style={{ maxWidth: '600px' }}>
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                By clicking &quot;Accept&quot;, you consent to our use of cookies.
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button
              variant="outline"
              color="gray"
              size="sm"
              onClick={close}
              disabled={isLoading}
            >
              Reject
            </Button>
            <Button
              size="sm"
              onClick={handleAccept}
              loading={isLoading}
            >
              Accept
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}; 